<script setup lang="ts">
/**
 * IBAN-edit flow inside the account panel. Three internal phases:
 *   form  →  request /account/iban/request
 *   otp   →  request /account/iban/confirm  →  done
 *   done  →  back to main
 *
 * Validation uses `useIban`'s pure helpers — backend remains the
 * authoritative validator. Errors map per HTTP status to the
 * locale strings under `portal.account_panel.errors.iban`.
 *
 * Mirrors the design bundle's `AccountPanel.jsx::PanelIban`.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { confirmIbanChange, requestIbanChange } from '@/api/portal'
import { isValidItalianIban } from '@/composables/useIban'
import type { PortalSelfResponse } from '@/stores/portalSelf.store'
import CdsOtpInput from '../atoms/CdsOtpInput.vue'

const props = defineProps<{
  partner: PortalSelfResponse
}>()

const emit = defineEmits<{
  back: []
  done: [partner: PortalSelfResponse]
}>()

const { t } = useI18n({ useScope: 'global' })

type Phase = 'form' | 'otp' | 'done'

const phase = ref<Phase>('form')
const iban = ref('')
const holder = ref(props.partner.account_holder)
const bank = ref(props.partner.bank)
const otpId = ref<string | null>(null)
const otp = ref('')
const maskedEmail = ref('')
const error = ref('')
const submitting = ref(false)
const demoCode = ref<string | null>(null)
const isDev = import.meta.env.DEV

const ibanValid = computed(() => isValidItalianIban(iban.value))
const showInvalidHint = computed(() => iban.value.length > 0 && !ibanValid.value)

function onIbanInput(e: Event): void {
  const target = e.target as HTMLInputElement
  iban.value = target.value.toUpperCase()
  target.value = iban.value
}

async function onRequest(e?: Event): Promise<void> {
  e?.preventDefault()
  if (submitting.value || !ibanValid.value) return
  error.value = ''
  submitting.value = true
  try {
    const r = await requestIbanChange({
      iban: iban.value,
      account_holder: holder.value,
      bank: bank.value,
    })
    otpId.value = r.data.otp_id
    maskedEmail.value = r.data.masked_email
    otp.value = ''
    phase.value = 'otp'
    demoCode.value = isDev ? (r.data.demo_code ?? null) : null
  } catch (err) {
    const status = readStatus(err)
    if (status === 400) {
      error.value = t('portal.account_panel.errors.iban.invalid_format')
    } else {
      error.value = t('portal.account_panel.errors.iban.generic')
    }
  } finally {
    submitting.value = false
  }
}

async function onConfirm(e?: Event): Promise<void> {
  e?.preventDefault()
  if (submitting.value || otp.value.length !== 6 || !otpId.value) return
  error.value = ''
  submitting.value = true
  try {
    const r = await confirmIbanChange({ otp_id: otpId.value, code: otp.value })
    phase.value = 'done'
    emit('done', r.data.partner)
  } catch (err) {
    const status = readStatus(err)
    if (status === 401) error.value = t('portal.account_panel.errors.iban.otp_invalid')
    else if (status === 410) error.value = t('portal.account_panel.errors.iban.otp_expired')
    else if (status === 429) error.value = t('portal.account_panel.errors.iban.too_many')
    else error.value = t('portal.account_panel.errors.iban.generic')
    otp.value = ''
  } finally {
    submitting.value = false
  }
}

function backToForm(): void {
  phase.value = 'form'
  error.value = ''
  otp.value = ''
}

function readStatus(err: unknown): number | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const r = (err as { response?: { status?: number } }).response
    return r?.status ?? null
  }
  return null
}
</script>

<template>
  <!-- Done -->
  <div v-if="phase === 'done'" class="pp-panel__done">
    <div class="pp-panel__done-icon">
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <h3>{{ t('portal.account_panel.iban.done.title') }}</h3>
    <p>{{ t('portal.account_panel.iban.done.body') }}</p>
    <button type="button" class="btn btn--primary" @click="emit('back')">
      <span>{{ t('portal.account_panel.iban.done.return') }}</span>
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    </button>
  </div>

  <!-- OTP -->
  <form v-else-if="phase === 'otp'" novalidate @submit="onConfirm">
    <p class="pp-panel__lede">
      {{ t('portal.account_panel.iban.otp_lede', { email: maskedEmail }) }}
    </p>
    <CdsOtpInput v-model="otp" :disabled="submitting" :error="!!error" />
    <div v-if="error" class="pp-auth__error pp-account-iban__error">
      {{ error }}
    </div>
    <div v-if="demoCode" class="pp-auth__demo-hint pp-account-iban__demo">
      <span>{{ t('portal.account_panel.iban.otp_demo_label') }}</span>
      <strong class="mono">{{ demoCode }}</strong>
    </div>
    <div class="pp-panel__foot">
      <button type="button" class="btn btn--ghost" @click="backToForm">
        {{ t('portal.account_panel.iban.otp_back') }}
      </button>
      <button type="submit" class="btn btn--primary" :disabled="submitting || otp.length !== 6">
        <span>{{
          submitting
            ? t('portal.account_panel.iban.otp_confirming')
            : t('portal.account_panel.iban.otp_confirm')
        }}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </div>
  </form>

  <!-- Form -->
  <form v-else novalidate @submit="onRequest">
    <div class="pp-panel__current">
      <div class="pp-panel__current-k">
        {{ t('portal.account_panel.iban.current_label') }}
      </div>
      <div class="pp-panel__current-v mono">{{ partner.iban }}</div>
    </div>
    <label class="pp-field">
      <span class="pp-field__label">
        {{ t('portal.account_panel.iban.new_label') }}
      </span>
      <input
        class="pp-field__input mono"
        :value="iban"
        :placeholder="t('portal.account_panel.iban.new_placeholder')"
        autofocus
        @input="onIbanInput"
      />
      <span :class="['pp-field__hint', { 'pp-field__hint--err': showInvalidHint }]">
        {{
          showInvalidHint
            ? t('portal.account_panel.iban.new_hint_invalid')
            : t('portal.account_panel.iban.new_hint_valid')
        }}
      </span>
    </label>
    <label class="pp-field">
      <span class="pp-field__label">
        {{ t('portal.account_panel.iban.holder_label') }}
      </span>
      <input v-model="holder" class="pp-field__input" />
    </label>
    <label class="pp-field">
      <span class="pp-field__label">
        {{ t('portal.account_panel.iban.bank_label') }}
      </span>
      <input v-model="bank" class="pp-field__input" />
    </label>
    <div v-if="error" class="pp-auth__error">{{ error }}</div>
    <div class="pp-panel__hint pp-account-iban__hint-spaced">
      {{ t('portal.account_panel.iban.otp_hint', { email: partner.email }) }}
    </div>
    <div class="pp-panel__foot">
      <button type="button" class="btn btn--ghost" @click="emit('back')">
        {{ t('portal.account_panel.iban.otp_back') }}
      </button>
      <button type="submit" class="btn btn--primary" :disabled="submitting || !ibanValid">
        <span>{{
          submitting
            ? t('portal.account_panel.iban.submitting')
            : t('portal.account_panel.iban.submit')
        }}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </div>
  </form>
</template>

<style scoped>
.pp-account-iban__error,
.pp-account-iban__demo {
  margin-top: var(--spacing-04);
}
.pp-account-iban__hint-spaced {
  margin-top: var(--spacing-04);
}
</style>
